function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { InvoiceUpdatedType, PusherEventName } from 'anchor-scout-common/types';
import { FullPageModalHeaderStart, Typography, Badge, ConfirmationModal, Callout, Button, useOverlayContext, Box } from 'seagull';
import { getStatusLabel } from "../../business-logic/invoiceLogic";
import { InvoiceAccountingIntegrationStatus } from "../InvoiceAccountingIntegrationStatus";
import { routes } from "../../constants";
import { useEventReporter, useIoContext, useStore, useStoreActions, useStoreStatic } from "../../contexts";
import { useUnmarkPaidExternally } from "../../hooks/invoice/useUnmarkPaidExternally";
import { useUnvoidInvoice } from "../../hooks/invoice/useUnvoidInvoice";
import { useAttachFiles } from "../../hooks/useAttachFiles";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { Intent } from "../../infra/access-control";
import { handlePromise, handleRequest } from "../../infra/utils";
import { invoiceModel, relationshipModel, timeModel } from "../../models";
import { makeManualApprovalMethodNoticePeriod } from "../../models/approval-method-notice-period";
import { InvoiceOptimisticUpdateType } from "../../providers/InvoiceChangesObservableProvider";
import { BusinessChannelEventBinder } from "../../pusher";
import { selectors } from "../../store/state-selectors";
import { Accounting } from "../../types/api.model";
import { Attachments } from "../Attachments/Attachments";
import styles from "./FullPageInvoice.module.scss";
import { getBadgeSkin, getFooter, onRequestPartialRefundSuccess, onRequestPaymentConfirmed, showRequestPaymentMethodModal } from "./FullPageInvoice.utils";
import FullPageInvoiceAccountingCallout from "./FullPageInvoiceAccountingCallout";
import FullPageInvoiceAdditionalActions from "./FullPageInvoiceAdditionalActions";
import { FullPageInvoiceCallout, getFullPageInvoiceCallouts, ResolvePaymentCallout } from "./FullPageInvoiceCallout";
import InvoiceLineItemsTable from "./InvoiceLineItemsTable/InvoiceLineItemsTable";
import RequestPartialRefund from "./RequestPartialRefund";
import StopPaymentModal from "./StopPaymentModal";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const approvePaymentLoader = 'approve-payment';
const retryChargeLoader = 'retry-charge';
const payNowLoader = 'pay-now';
const stopPaymentLoader = 'stopPayment';
const resumePaymentLoader = 'resumePayment';
export default function FullPageInvoiceWithData(props) {
  const {
    relationship,
    isVendor,
    showQboIntegration,
    showInvoiceQboSection,
    showInvoiceXeroSection,
    showXeroIntegration,
    contacts,
    serviceTemplates,
    refresh,
    onRefresh,
    onRedirectToPaymentMethods
  } = props;
  const overlayContext = useOverlayContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const history = useHistory();
  const {
    reportButtonClick
  } = useEventReporter();
  const {
    getNow
  } = useIoContext();
  const now = useMemo(() => getNow(), []);
  const {
    getState
  } = useStoreStatic();
  const [invoice, setInvoice] = useState(props.invoice);
  const [disablingAutoApprove, setDisablingAutoApprove] = useState(false);
  const {
    startUnvoidInvoice,
    finishUnvoidInvoice
  } = useUnvoidInvoice();
  const {
    startUnmarkPaidExternally,
    finishUnmarkPaidExternally
  } = useUnmarkPaidExternally();
  const {
    attachments,
    openFilePicker,
    handleRemove: handleRemoveAttachment,
    isUploading: isUploadingAttachments
  } = useAttachFiles({
    initialAttachments: invoice.attachments,
    fetchAttachmentUrl: attachmentId => api.invoice.getAttachmentDownloadUrl(invoice.id, attachmentId),
    onFilesUploaded: uploadedData => api.invoice.attachFiles(invoice.id, uploadedData),
    onRemove: attachment => api.invoice.removeAttachment(invoice.id, attachment.id)
  });
  const isPendingQBOIntegrationSetup = useStore(selectors.integration.isPendingAccountingIntegrationSetup(Accounting.QBO));
  const isPendingXeroIntegrationSetup = useStore(selectors.integration.isPendingAccountingIntegrationSetup(Accounting.Xero));
  const {
    name,
    postalAddress
  } = relationship.vendor;
  const vendorLogoUrl = relationshipModel.vendorLogoUrl(relationship);
  const {
    addressOne,
    addressTwo,
    city,
    state,
    postalCode
  } = postalAddress;
  const {
    XERO_INTEGRATION = false,
    ATTACH_FILES,
    UNVOID_INVOICE,
    UNMARK_PAID_EXTERNALLY,
    HEADER_LAYOUT_UPDATES = false
  } = useFeatureFlags();
  useEffect(() => {
    setInvoice(props.invoice);
  }, [props.invoice]);
  async function reloadInvoice() {
    refresh().then(() => onRefresh({}));
  }
  const onDisableAutoApprove = async invoice => {
    setDisablingAutoApprove(true);
    try {
      await api.invoice.disableAutoApprove(invoiceModel.id(invoice));
      overlayContext.showSuccessToast('Disabled auto-approval', 'disable-auto-approval-success');
      setInvoice(invoiceModel.setApprovalMethod(invoice, makeManualApprovalMethodNoticePeriod()));
    } catch (_unused) {
      overlayContext.showErrorToast('Failed to disable auto-approval', 'disable-auto-approval-error');
    } finally {
      setDisablingAutoApprove(false);
    }
  };
  function onRetryCharge() {
    overlayContext.showModal({
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          skin: "danger",
          title: "Retry charge of failed payment",
          textAlignCenter: false,
          content: /*#__PURE__*/_jsx(_Fragment, {
            children: /*#__PURE__*/_jsxs(Typography, {
              "data-testid": "retry-charge-modal-text",
              children: ["You may retry the charge up to four times.", ' ', isVendor && 'It is recommended to inform your client before proceeding so they can verify funds are available.', /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx("br", {}), "Are you sure you want to charge invoice #", invoice.formattedNumber, " now?"]
            })
          }),
          onPrimaryButtonClick: async () => {
            overlayContext.startLoader(retryChargeLoader);
            const {
              success
            } = await handleRequest(api.invoice.retryCharge(invoice.id));
            if (!success) {
              overlayContext.showErrorToast('Unable to retry, please try again', 'retry-charge-error');
              overlayContext.stopLoader(retryChargeLoader);
              return;
            }
            onRequestPaymentConfirmed({
              invoice,
              setInvoice,
              onRefresh,
              isVendor
            });
            overlayContext.stopLoader(retryChargeLoader);
            dismissModal();
          },
          onSecondaryButtonClick: () => dismissModal(),
          onCloseButtonClick: () => dismissModal(),
          "data-testid": "retry-charge-confirmation-modal",
          primaryButtonLabel: "Charge invoice #".concat(invoice.formattedNumber),
          primaryButtonIntent: Intent.InvoiceRetryCharge,
          secondaryButtonLabel: "Cancel"
        });
      }
    });
  }
  async function onApprovePayment() {
    logBI(10564, {
      invoiceId: invoice.id,
      relationshipId: relationship.id
    });
    reportButtonClick('billing-hub-client-approved-payment');
    overlayContext.startLoader(approvePaymentLoader);
    const {
      success
    } = await handleRequest(api.invoice.approve(invoice.id));
    if (success) {
      // Optimistic update.
      setInvoice(invoiceModel.setPending(invoice, now));
      onRefresh({
        optimisticUpdate: {
          type: InvoiceOptimisticUpdateType.PaymentApproved,
          invoiceId: invoice.id
        }
      });
    } else {
      overlayContext.showErrorToast("Failed to approve invoice ".concat(invoice.formattedNumber), 'approve-payment-error');
    }
    overlayContext.stopLoader(approvePaymentLoader);
  }
  async function onPayNow() {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          textAlignCenter: false,
          onPrimaryButtonClick: async () => {
            logBI(10561, {
              invoiceId: invoice.id
            });
            overlayContext.startLoader(payNowLoader);
            const {
              success
            } = await handlePromise(api.invoice.payNow(invoice.id));
            if (!success) {
              overlayContext.showErrorToast('Unable to pay now, please try again', 'pay-now-error');
              overlayContext.stopLoader(payNowLoader);
              return;
            }
            await handleRequest(reloadInvoice());
            overlayContext.stopLoader(payNowLoader);
            dismissModal();
          },
          onCloseButtonClick: () => dismissModal(),
          onSecondaryButtonClick: () => dismissModal(),
          title: "Pay this invoice now?",
          content: /*#__PURE__*/_jsxs(Typography, {
            children: ["If you pay this invoice now, we will process the payment immediately. The due date will change to today's date, ", timeModel.formatDate(now.getTime()), ".", /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx("br", {}), "If you prefer payment on the original due date ", invoiceModel.dueDate(invoice), ", click \"Cancel\"."]
          }),
          primaryButtonLabel: "I agree",
          primaryButtonTestid: "invoice-pay-now-confirm",
          secondaryButtonLabel: "Cancel"
        });
      }
    });
  }
  function onStopPayment() {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: props => /*#__PURE__*/_jsx(StopPaymentModal, _objectSpread(_objectSpread({}, props), {}, {
        formattedNumber: invoice.formattedNumber,
        onSubmit: async reason => {
          const logStopPaymentBI = success => logBI(10562, {
            invoiceId: invoice.id,
            success
          });
          overlayContext.startLoader(stopPaymentLoader);
          const {
            success
          } = await handleRequest(api.invoice.stopPayment(invoice.id, reason));
          if (!success) {
            logStopPaymentBI(false);
            overlayContext.stopLoader(stopPaymentLoader);
            overlayContext.showErrorToast("Failed to stop payment for invoice ".concat(invoice.formattedNumber), 'fpi-stop-payment-error');
            return {
              success: false
            };
          } else {
            logStopPaymentBI(true);
            await handleRequest(reloadInvoice());
            overlayContext.stopLoader(stopPaymentLoader);
            return {
              success: true
            };
          }
        }
      }))
    });
  }
  function onResumePayment() {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          title: "Resume Payment",
          textAlignCenter: false,
          primaryButtonLabel: "Resume",
          primaryButtonTestid: "submit-resume-payment",
          secondaryButtonLabel: "Back",
          content: /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsxs(Typography, {
              children: ["You're about to resume a payment for invoice ", invoice.formattedNumber, "."]
            }), /*#__PURE__*/_jsx(Typography, {
              children: "Any future invoices for this service provider will resume payment collection."
            })]
          }),
          onSecondaryButtonClick: () => dismissModal(),
          onPrimaryButtonClick: async () => {
            const logResumePaymentBI = success => logBI(10563, {
              invoiceId: invoice.id,
              success
            });
            overlayContext.startLoader(resumePaymentLoader);
            const {
              success
            } = await handleRequest(api.invoice.resumePayment(invoice.id));
            if (!success) {
              logResumePaymentBI(false);
              overlayContext.stopLoader(resumePaymentLoader);
              overlayContext.showErrorToast("Failed to resume payment for invoice ".concat(invoice.formattedNumber), 'fpi-resume-payment-error');
            } else {
              logResumePaymentBI(true);
              await handleRequest(reloadInvoice());
              overlayContext.stopLoader(resumePaymentLoader);
            }
            dismissModal();
          }
        });
      }
    });
  }
  async function onVoidInvoice() {
    var _getState$userInfo;
    const confirmed = await overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          title: "Void this invoice",
          "data-testid": "fpi-void-invoice-modal",
          content: "Are you sure you want to void invoice ".concat(invoice.formattedNumber, "?"),
          onPrimaryButtonClick: () => dismissModal(true),
          onSecondaryButtonClick: () => dismissModal(false),
          primaryButtonLabel: "I'm sure",
          primaryButtonTestid: "fpi-void-invoice-confirm",
          secondaryButtonLabel: "Cancel"
        });
      }
    });
    if (!confirmed) return;
    const loaderName = 'void';
    try {
      overlayContext.startLoader(loaderName);
      await api.invoice.void(invoice.id);
    } catch (err) {
      overlayContext.showErrorToast("Failed to void invoice ".concat(invoice.formattedNumber), 'fpi-void-invoice-error');
      return;
    } finally {
      overlayContext.stopLoader(loaderName);
    }

    // Optimistic update.
    const userId = ((_getState$userInfo = getState().userInfo) === null || _getState$userInfo === void 0 ? void 0 : _getState$userInfo.id()) || '';
    setInvoice(invoiceModel.setVoided(invoice, userId));
    onRefresh({
      optimisticUpdate: {
        type: InvoiceOptimisticUpdateType.Voided,
        invoiceId: invoice.id,
        userId
      }
    });
  }
  function onRequestRefund() {
    const payment = invoiceModel.payment(invoice);
    if (!payment) return;
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref5 => {
        let {
          dismissModal
        } = _ref5;
        return /*#__PURE__*/_jsx(RequestPartialRefund, {
          relationship: relationship,
          id: invoice.id,
          rawTotalAmount: invoiceModel.rawTotalAmount(invoice),
          dismissModal: dismissModal,
          payment: payment,
          refreshInvoice: refresh,
          onSuccess: _ref6 => {
            let {
              reason,
              refundAmount,
              refundFees
            } = _ref6;
            return onRequestPartialRefundSuccess({
              invoice,
              sellerId: relationshipModel.sellerID(relationship),
              reason,
              now: getNow(),
              setInvoice,
              onRefresh,
              refundAmount,
              refundFees
            });
          }
        });
      }
    });
  }
  function handleQboIntegrationChange(integration) {
    onRefresh({
      optimisticUpdate: {
        type: InvoiceOptimisticUpdateType.QboIntegrationChanged,
        invoiceId: invoice.id,
        integration
      }
    });
  }
  function handleXeroIntegrationChange(integration) {
    onRefresh({
      optimisticUpdate: {
        type: InvoiceOptimisticUpdateType.XeroIntegrationChanged,
        invoiceId: invoice.id,
        xeroIntegration: integration
      }
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    className: styles.root,
    "data-testid": "fpi-content",
    children: [/*#__PURE__*/_jsx(BusinessChannelEventBinder, {
      source: "scout",
      eventName: PusherEventName.InvoiceUpdated,
      callback: payload => {
        if (invoice.id !== payload.invoice.id) {
          return;
        }
        if (payload.type === InvoiceUpdatedType.Unvoided && UNVOID_INVOICE) {
          setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
          finishUnvoidInvoice(invoice.id);
        } else if (payload.type === InvoiceUpdatedType.UnmarkedAsPaidExternally && UNMARK_PAID_EXTERNALLY) {
          setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
          finishUnmarkPaidExternally(invoice.id);
        } else if (payload.type === InvoiceUpdatedType.RefundFailed) {
          setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
        } else if (payload.type === InvoiceUpdatedType.XeroIntegrationStatusUpdated && XERO_INTEGRATION) {
          setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
        } else if (payload.type === InvoiceUpdatedType.QboIntegrationStatusUpdated) {
          setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
        }
      }
    }), /*#__PURE__*/_jsx(FullPageModalHeaderStart, {
      children: /*#__PURE__*/_jsxs("div", {
        className: styles.invoiceHeader,
        children: [/*#__PURE__*/_jsxs(Box, {
          children: ["Invoice #", invoice.formattedNumber]
        }), /*#__PURE__*/_jsx(Badge, {
          outlined: true,
          rounded: HEADER_LAYOUT_UPDATES,
          skin: getBadgeSkin(invoice.displayStatus),
          "data-testid": "fpi-status",
          "data-intent": invoice.displayStatus,
          children: getStatusLabel(invoice.displayStatus)
        }), showInvoiceQboSection && /*#__PURE__*/_jsx(InvoiceAccountingIntegrationStatus, {
          className: styles.qboIcon,
          testid: "fpi-qbo-icon",
          invoiceIntegration: invoiceModel.qboIntegration(invoice),
          placement: "right",
          accountingType: Accounting.QBO
        }), showInvoiceXeroSection && /*#__PURE__*/_jsx(InvoiceAccountingIntegrationStatus, {
          className: styles.xeroIcon,
          testid: "fpi-xero-icon",
          invoiceIntegration: invoiceModel.xeroIntegration(invoice),
          placement: "right",
          accountingType: Accounting.Xero
        })]
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.calloutsSection,
      children: [showRequestPaymentMethodModal(invoice, relationship) && /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": "fpi-missing-pm",
        withAction: /*#__PURE__*/_jsx(Button, {
          skin: "primaryOutline",
          "data-testid": "fpi-add-pm",
          onClick: onRedirectToPaymentMethods,
          children: "Add payment method"
        }),
        children: "For this invoice to get paid through Anchor, a payment method must be connected"
      }), invoiceModel.isPaymentMethodUnverified(invoice) && !relationshipModel.isTerminated(relationship) && /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": "fpi-unverified-pm",
        withAction: /*#__PURE__*/_jsx(Button, {
          skin: "primaryOutline",
          "data-testid": "fpi-verify-pm",
          onClick: onRedirectToPaymentMethods,
          children: "Verify payment method"
        }),
        children: "The payment method connected to this agreement is not yet verified"
      }), isPendingQBOIntegrationSetup && showQboIntegration && /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": "fpi-qbo-setup",
        withAction: /*#__PURE__*/_jsx(Button, {
          "data-testid": "fpi-go-to-qbo-setup",
          skin: "primaryOutline",
          onClick: () => history.push(routes.qboSetup),
          children: "Go to setup"
        }),
        children: "Please finish setting up QBO by selecting the accounts in the integration setup page"
      }), isPendingXeroIntegrationSetup && showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": "fpi-xero-setup",
        withAction: /*#__PURE__*/_jsx(Button, {
          "data-testid": "fpi-go-to-xero-setup",
          skin: "primaryOutline",
          onClick: () => history.push(routes.xeroSetup),
          children: "Go to setup"
        }),
        children: "Please finish setting up Xero by selecting the accounts in the integration setup page"
      }), showInvoiceQboSection && /*#__PURE__*/_jsx(FullPageInvoiceAccountingCallout, {
        relationship: relationship,
        invoice: invoice,
        contacts: contacts,
        serviceTemplates: serviceTemplates,
        reloadInvoice: reloadInvoice,
        onChange: handleQboIntegrationChange,
        accounting: Accounting.QBO,
        integration: invoiceModel.accounting({
          accounting: Accounting.QBO,
          invoice
        }).integration
      }), showInvoiceXeroSection && /*#__PURE__*/_jsx(FullPageInvoiceAccountingCallout, {
        relationship: relationship,
        invoice: invoice,
        contacts: contacts,
        serviceTemplates: serviceTemplates,
        reloadInvoice: reloadInvoice,
        onChange: handleXeroIntegrationChange,
        accounting: Accounting.Xero,
        integration: invoiceModel.accounting({
          accounting: Accounting.Xero,
          invoice
        }).integration
      }), getFullPageInvoiceCallouts({
        invoice,
        relationship,
        isVendor,
        disabling: disablingAutoApprove,
        now,
        onRetryCharge,
        onApprovePayment,
        onPayNow,
        onResumePayment,
        onDisableAutoApprove
      }).map(props => /*#__PURE__*/_jsx(FullPageInvoiceCallout, _objectSpread({}, props), props.id)), invoiceModel.isPaymentActionRequired(invoice) && /*#__PURE__*/_jsx(ResolvePaymentCallout, {
        invoice: invoice,
        isVendor: isVendor,
        reloadInvoice: reloadInvoice
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.titleSection,
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "h2",
        children: "Invoice"
      }), vendorLogoUrl && /*#__PURE__*/_jsx("img", {
        className: styles.logo,
        src: vendorLogoUrl
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.titleSection,
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.invoiceDetails,
        children: [['Client name:', relationship.customer.name], ['Invoice number:', invoiceModel.formattedNumber(invoice)], ['Issue date:', invoiceModel.issuedOn(invoice)], ['Due date:', invoiceModel.dueDate(invoice)]].map((_ref7, idx) => {
          let [label, value] = _ref7;
          return /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx(Typography, {
              children: label
            }, idx + 'l'), /*#__PURE__*/_jsx(Typography, {
              children: value
            }, idx + 'v')]
          }, idx);
        })
      }), /*#__PURE__*/_jsx("div", {
        className: styles.vendorDetails,
        children: compact([name, addressOne, addressTwo, "".concat(city, ", ").concat(state, ", ").concat(postalCode)]).map((text, idx) => /*#__PURE__*/_jsx(Typography, {
          children: text
        }, idx))
      })]
    }), /*#__PURE__*/_jsx(InvoiceLineItemsTable, {
      invoice: invoice,
      isVendor: isVendor
    }), ATTACH_FILES && attachments.length > 0 && /*#__PURE__*/_jsx(Attachments, {
      files: attachments,
      onRemove: handleRemoveAttachment
    }), getFooter(relationship, invoice).map(_ref8 => {
      let {
        id,
        text
      } = _ref8;
      return /*#__PURE__*/_jsx(Typography, {
        variant: "paragraph2",
        children: text
      }, id);
    }), /*#__PURE__*/_jsx(FullPageInvoiceAdditionalActions, {
      invoice: invoice,
      relationship: relationship,
      isVendor: isVendor,
      reloadInvoice: reloadInvoice,
      onVoidInvoice: onVoidInvoice,
      onUnvoidInvoice: () => startUnvoidInvoice(invoice),
      onUnmarkPaidExternally: () => startUnmarkPaidExternally(invoice),
      onStopPayment: onStopPayment,
      onResumePayment: onResumePayment,
      onRequestRefund: onRequestRefund,
      onAttachFilesClick: openFilePicker,
      isUploadingAttachments: isUploadingAttachments
    })]
  });
}